import { useState } from 'react';

interface ReasonDialogProps {
  title: string;
  description?: string;
  confirmLabel: string;
  danger?: boolean;
  busy: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * The one shape every money/entitlement-moving action in this app uses: a
 * MANDATORY free-text reason, confirm/cancel, and a busy state that disables
 * both while the request is in flight (proposal §4: "reason is required...
 * for every action that moves money, grants entitlement, or deletes data").
 * A single component so every dialog enforces the same rule the same way,
 * rather than each call site re-deriving "is the reason empty".
 */
export function ReasonDialog({
  title,
  description,
  confirmLabel,
  danger,
  busy,
  onConfirm,
  onClose,
}: ReasonDialogProps) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40"
    >
      <div className="w-full max-w-md rounded-card border border-border bg-background p-6 shadow-lg">
        <h2 className="text-h3 font-semibold text-fg-primary">{title}</h2>
        {description ? <p className="mt-2 text-body text-fg-secondary">{description}</p> : null}
        <label
          className="mt-4 block text-meta font-medium text-fg-secondary"
          htmlFor="reason-dialog-input"
        >
          Reason
        </label>
        <textarea
          id="reason-dialog-input"
          className="mt-1 w-full rounded-card border border-border bg-surface-field p-2 text-body text-fg-primary"
          rows={3}
          value={reason}
          disabled={busy}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this action being taken? (shown in the audit log and forwarded to payment-back)"
        />
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-card px-3 py-2 text-body text-fg-secondary hover:bg-state-hover"
            disabled={busy}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={
              danger
                ? 'rounded-card bg-destructive px-3 py-2 text-body font-medium text-destructive-foreground hover:bg-destructive-hover disabled:opacity-50'
                : 'rounded-card bg-primary px-3 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50'
            }
            disabled={busy || !trimmed}
            onClick={() => onConfirm(trimmed)}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
